import { Box, HStack, Tag, TagLabel, Text, VStack } from "@chakra-ui/react";
import { format } from "date-fns";
import FailureReason from "components/FailureReason";
import { formatDuration } from "components/tasks/taskMeta";
import type {
  MFApprovalRequestLine,
  MFRequestLine,
} from "state/members/access_v2/catalogs";
import {
  groupLines,
  LINE_STATUS_COLOR,
  LINE_STATUS_LABEL,
} from "state/members/access_v2/roleRequests";

const isApproverLine = (
  line: MFRequestLine | MFApprovalRequestLine,
): line is MFApprovalRequestLine => "coveredByStage" in line;

// How long the line lasts: until its end once it has one, else what was asked.
const durationDetail = (line: MFRequestLine): string | null => {
  if (line.endedAt) return null;
  if (line.expiresAt)
    return `Until ${format(new Date(line.expiresAt), "PPP p")}`;
  if (line.requestedIndefinite) return "Indefinitely";
  return line.requestedForSeconds
    ? `For ${formatDuration(line.requestedForSeconds)}`
    : null;
};

const LineRow = ({
  line,
  showStatus,
  sideEffect,
}: {
  line: MFRequestLine | MFApprovalRequestLine;
  showStatus: boolean;
  sideEffect?: boolean;
}) => {
  const approver = isApproverLine(line) ? line : null;
  const failure = line.failure ?? null;
  const endReasonIsHeadline =
    failure !== null &&
    (failure.message === null || failure.message === line.endReason);
  const detail = [
    durationDetail(line),
    endReasonIsHeadline ? null : line.endReason,
    approver?.decidedBy ? `Decided by ${approver.decidedBy.name}` : null,
    line.claimed !== "none"
      ? "Takes over access held outside Ploy, removed when the request ends"
      : null,
  ].filter(Boolean);

  return (
    <HStack w="full" justify="space-between" align="flex-start" spacing={3}>
      <VStack align="flex-start" spacing={0}>
        <Text fontSize="sm" fontWeight={sideEffect ? 400 : 500}>
          {sideEffect ? `Also gives ${line.targetName}` : line.targetName}
        </Text>
        {detail.map((d) => (
          <Text key={d} variant="lil">
            {d}
          </Text>
        ))}
        {failure && (
          <FailureReason
            failure={failure}
            fallbackHeadline={endReasonIsHeadline ? line.endReason : null}
          />
        )}
      </VStack>
      <HStack spacing={1} flexShrink={0}>
        {approver?.coveredByStage && (
          <Tag size="sm" colorScheme="blue">
            <TagLabel>This stage</TagLabel>
          </Tag>
        )}
        {showStatus && (
          <Tag size="sm" colorScheme={LINE_STATUS_COLOR[line.status]}>
            <TagLabel>{LINE_STATUS_LABEL[line.status]}</TagLabel>
          </Tag>
        )}
      </HStack>
    </HStack>
  );
};

// The roles a request names, each with what its route also gives beneath it.
const RequestLines = ({
  lines,
  showStatus = true,
}: {
  lines: (MFRequestLine | MFApprovalRequestLine)[] | null | undefined;
  showStatus?: boolean;
}) => {
  const groups = groupLines(lines);
  if (groups.length === 0) return null;

  return (
    <VStack w="full" align="flex-start" spacing={3}>
      {groups.map(({ line, sideEffects }) => (
        <VStack key={line.id} w="full" align="flex-start" spacing={2}>
          <LineRow line={line} showStatus={showStatus} />
          {sideEffects.length > 0 && (
            <Box w="full" pl={3} borderLeft="2px solid" borderColor="gray.200">
              <VStack w="full" align="flex-start" spacing={2}>
                {sideEffects.map((effect) => (
                  <LineRow
                    key={effect.id}
                    line={effect}
                    showStatus={showStatus}
                    sideEffect
                  />
                ))}
              </VStack>
            </Box>
          )}
        </VStack>
      ))}
    </VStack>
  );
};

export default RequestLines;
